import ClearAllIcon from "@mui/icons-material/ClearAll";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Chip,
	IconButton,
	Paper,
	Tab,
	Tabs,
	Tooltip,
	Typography,
} from "@mui/material";
import type { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useState } from "react";
import type { ExecutionResult } from "../models/SPExecutorModels";

type ResultSet = {
	rows: any[];
	columns: string[];
};

type Props = {
	results: ExecutionResult[];
	onDeleteResult: (id: string) => void;
	onClearAll: () => void;
};

const defaultColDef = {
	flex: 1,
	filter: false,
	sortable: true,
	resizable: true,
};

const parseResults = (data: any): ResultSet[] => {
	if (!data || !data.tables) return [];

	return data.tables.map((table: any) => {
		const rows = table.rows || [];
		const columns =
			rows.length > 0
				? Object.keys(rows[0])
				: table.columns?.map((c: any) => c.name) || [];
		return { rows, columns };
	});
};

const formatTimestamp = (date: Date): string => {
	return date.toLocaleTimeString("en-US", {
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
	});
};

const formatDuration = (ms: number): string => {
	if (ms < 1000) return `${ms}ms`;
	return `${(ms / 1000).toFixed(2)}s`;
};

const formatParameters = (params: { name: string; value: any }[]): string => {
	if (params.length === 0) return "No parameters";
	return params
		.map((p) => `${p.name}=${p.value === null ? "NULL" : p.value}`)
		.join(", ");
};

const ResultItem = ({
	result,
	onDelete,
	defaultExpanded,
}: {
	result: ExecutionResult;
	onDelete: () => void;
	defaultExpanded: boolean;
}) => {
	const [activeTab, setActiveTab] = useState(0);
	const resultSets = useMemo(() => parseResults(result.data), [result.data]);

	const getColumnDefs = (columns: string[]): ColDef[] => {
		return columns.map((col) => ({
			field: col,
			headerName: col,
			minWidth: 100,
			flex: 1,
			cellRenderer: (params: any) => {
				const value = params.value;
				if (value === null || value === undefined) {
					return (
						<span style={{ color: "#999", fontStyle: "italic" }}>NULL</span>
					);
				}
				if (typeof value === "boolean") {
					return value ? "true" : "false";
				}
				return String(value);
			},
		}));
	};

	const getTotalRows = (): number => {
		return resultSets.reduce((sum, rs) => sum + rs.rows.length, 0);
	};

	return (
		<Accordion defaultExpanded={defaultExpanded}>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon />}
				sx={{
					bgcolor: result.isError ? "error.light" : "background.paper",
					"&:hover": { bgcolor: result.isError ? "error.light" : "grey.100" },
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						width: "100%",
						gap: 2,
						pr: 2,
					}}
				>
					<Typography
						variant="subtitle2"
						sx={{ fontWeight: 600, minWidth: 80 }}
					>
						{formatTimestamp(result.timestamp)}
					</Typography>
					<Typography
						variant="body2"
						sx={{
							flexGrow: 1,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{result.procedureName}
					</Typography>
					<Chip
						label={formatDuration(result.duration)}
						size="small"
						color={result.duration > 5000 ? "warning" : "default"}
						sx={{ minWidth: 60 }}
					/>
					{!result.isError && (
						<Chip
							label={`${getTotalRows()} rows`}
							size="small"
							color="primary"
							variant="outlined"
							sx={{ minWidth: 70 }}
						/>
					)}
					{result.isError && (
						<Chip
							label="Error"
							size="small"
							color="error"
							sx={{ minWidth: 60 }}
						/>
					)}
					<Tooltip title="Delete this result">
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								onDelete();
							}}
							sx={{ ml: 1 }}
						>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			</AccordionSummary>
			<AccordionDetails>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ mb: 1, display: "block" }}
				>
					Parameters: {formatParameters(result.parameters)}
				</Typography>

				{result.isError ? (
					<Typography color="error" variant="body2">
						{result.errorMessage}
					</Typography>
				) : resultSets.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						The stored procedure executed successfully but returned no result
						sets.
					</Typography>
				) : (
					<>
						{resultSets.length > 1 && (
							<Tabs
								value={activeTab}
								onChange={(_, newValue) => setActiveTab(newValue)}
								sx={{ mb: 1 }}
							>
								{resultSets.map((rs, index) => (
									<Tab
										key={index}
										label={`Result Set ${index + 1} (${rs.rows.length})`}
									/>
								))}
							</Tabs>
						)}

						{resultSets.map((resultSet, index) => (
							<Box
								key={index}
								sx={{
									display: activeTab === index ? "block" : "none",
									height: 300,
								}}
							>
								{resultSet.rows.length === 0 ? (
									<Typography variant="body2" color="text.secondary">
										No rows returned.
									</Typography>
								) : (
									<AgGridReact
										rowData={resultSet.rows}
										columnDefs={getColumnDefs(resultSet.columns)}
										defaultColDef={defaultColDef}
										domLayout="normal"
									/>
								)}
							</Box>
						))}
					</>
				)}
			</AccordionDetails>
		</Accordion>
	);
};

export const ResultsList = ({ results, onDeleteResult, onClearAll }: Props) => {
	if (results.length === 0) {
		return (
			<Paper sx={{ p: 3, textAlign: "center" }}>
				<Typography variant="body2" color="text.secondary">
					Execute a stored procedure to see results here.
				</Typography>
			</Paper>
		);
	}

	return (
		<Paper sx={{ p: 2 }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
				}}
			>
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					Execution Results ({results.length})
				</Typography>
				<Button
					variant="outlined"
					color="error"
					size="small"
					startIcon={<ClearAllIcon />}
					onClick={onClearAll}
				>
					Clear All
				</Button>
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
				{results.map((result, index) => (
					<ResultItem
						key={result.id}
						result={result}
						onDelete={() => onDeleteResult(result.id)}
						defaultExpanded={index === 0}
					/>
				))}
			</Box>
		</Paper>
	);
};
